/**
 * BOOMBA AU - Subscribe upsell inside the AMP cart drawer.
 *
 * Lets a customer turn a one-time line into a Recharge subscription without leaving
 * the cart, then pick the delivery frequency from a dropdown.
 *
 * Kept in its own asset on purpose:
 *   - amp-upsell.js stays byte-identical, so rollback is deleting this file plus one
 *     loader line in theme.liquid
 *   - everything here is namespaced under window.BOOMBA_SUB_UPSELL for debugging
 *
 * Must be loaded AFTER amp-upsell.js in theme.liquid. Both are `defer`, so they run in
 * document order and this file's AMP_CART_UPDATED assignment wins.
 */
(function () {
  "use strict";

  // ---------------------------------------------------------------------------
  // Config - everything tweakable lives here
  // ---------------------------------------------------------------------------

  const SELECTORS = {
    cartLine: ".amp-cart__cart-line",
    cartLineDetails: ".amp-cart__cart-line-details",
    drawer: ".amp-cart-drawer",
    ampRoot: "#amp-theme-extension",
  };

  const COPY = {
    upgradePrompt: "Upgrade to subscription and save up to {discount}%",
    upgradePromptNoDiscount: "Upgrade to subscription",
    frequencyOption: "{cadence}: save {discount}%",
    frequencyOptionNoDiscount: "{cadence}",
    frequencyLabel: "Deliver every",
    oneTimeOption: "One-time purchase",
    // Only ever rendered on the upgrade button - the frequency dropdown shows a spinner
    // instead - so it can name the upgrade specifically.
    pending: "Upgrading...",
    error: "Could not update. Please try again.",
  };

  const ERROR_VISIBLE_MS = 6000;

  // AMP repaints the drawer a few seconds after change.js, and the repaint is what clears
  // the pending state. If it never comes, hand the control back rather than leaving it dead.
  const PENDING_TIMEOUT_MS = 8000;

  // Subscribers should not lose a free gift they already unlocked just because the
  // subscription discount pushed the cart under the reward threshold. Flip to false to
  // fall back to the stock amp-upsell.js behaviour.
  const KEEP_FREE_GIFT_FOR_SUBSCRIBERS = true;

  const INJECTED_ROW_CLASS = "boomba-sub-upsell";
  const INJECTED_ROW_FLAG = "boombaSubUpsell";

  // Stamped on the AMP line element, not on our row - see renderLine.
  const LINE_KEY_FLAG = "boombaSubUpsellKey";

  // ---------------------------------------------------------------------------
  // Debug helpers - enable in console with:
  //   localStorage.setItem("boomba_sub_upsell_debug", "1")
  // ---------------------------------------------------------------------------

  const isDebugEnabled = (function () {
    try {
      return localStorage.getItem("boomba_sub_upsell_debug") === "1";
    } catch (error) {
      return false;
    }
  })();

  function log() {
    if (!isDebugEnabled) {
      return;
    }

    console.log.apply(console, ["[boomba-sub-upsell]"].concat([].slice.call(arguments)));
  }

  // ---------------------------------------------------------------------------
  // Selling plan lookup
  // ---------------------------------------------------------------------------

  // product handle -> parsed /products/<handle>.js payload
  const productCache = new Map();

  function fetchProduct(handle) {
    if (productCache.has(handle)) {
      return productCache.get(handle);
    }

    const request = fetch(window.Shopify.routes.root + "products/" + handle + ".js")
      .then(function (response) {
        return response.ok ? response.json() : null;
      })
      .catch(function (error) {
        log("product fetch failed", handle, error);
        return null;
      });

    productCache.set(handle, request);

    return request;
  }

  /**
   * Recharge exposes the human-readable cadence as a selling plan option, e.g.
   * { name: "Order Frequency and Unit", value: "3-month" }. Returns just the cadence
   * ("3 months") because the "Deliver every" wording lives in the row label. Falls back
   * to the plan name so a renamed option never blanks the dropdown.
   */
  function buildFrequencyLabel(sellingPlan) {
    const options = sellingPlan.options || [];

    const frequencyOption = options.find(function (option) {
      return /frequency/i.test(option.name || "");
    });

    if (!frequencyOption || !frequencyOption.value) {
      return sellingPlan.name;
    }

    const parts = String(frequencyOption.value).split("-");
    const interval = parseInt(parts[0], 10);
    const unit = parts[1] || "";

    if (!interval || !unit) {
      return sellingPlan.name;
    }

    const pluralUnit = interval > 1 ? unit + "s" : unit;

    return interval + " " + pluralUnit;
  }

  function readDiscountPercent(sellingPlan) {
    const adjustments = sellingPlan.price_adjustments || [];

    const percentageAdjustment = adjustments.find(function (adjustment) {
      return adjustment.value_type === "percentage";
    });

    return percentageAdjustment ? Number(percentageAdjustment.value) : 0;
  }

  /**
   * Only the plans actually allocated to THIS variant are offered. A product-level group
   * can exclude variants, so reading selling_plan_groups alone would show dead options.
   */
  function resolvePlansForVariant(product, variantId) {
    if (!product) {
      return [];
    }

    const variant = (product.variants || []).find(function (candidate) {
      return candidate.id === variantId;
    });

    if (!variant || !variant.selling_plan_allocations) {
      return [];
    }

    const allowedPlanIds = variant.selling_plan_allocations.map(function (allocation) {
      return allocation.selling_plan_id;
    });

    const plans = [];

    (product.selling_plan_groups || []).forEach(function (group) {
      (group.selling_plans || []).forEach(function (sellingPlan) {
        if (allowedPlanIds.indexOf(sellingPlan.id) === -1) {
          return;
        }

        plans.push({
          id: sellingPlan.id,
          label: buildFrequencyLabel(sellingPlan),
          discountPercent: readDiscountPercent(sellingPlan),
        });
      });
    });

    return plans;
  }

  // ---------------------------------------------------------------------------
  // Cart mutation
  // ---------------------------------------------------------------------------

  // Guards against a double POST while AMP is mid-refresh.
  let isApplyingPlan = false;

  function applySellingPlan(lineNumber, quantity, sellingPlanId) {
    if (isApplyingPlan) {
      return Promise.resolve(null);
    }

    isApplyingPlan = true;

    const payload = {
      line: lineNumber,
      quantity: quantity,
      selling_plan: sellingPlanId,
    };

    log("applying", payload);

    return fetch(window.Shopify.routes.root + "cart/change.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    })
      .then(function (response) {
        return response.json();
      })
      .catch(function (error) {
        console.error("[boomba-sub-upsell] change.js failed", error);
        return null;
      })
      .finally(function () {
        isApplyingPlan = false;
      });
  }

  // ---------------------------------------------------------------------------
  // UI
  // ---------------------------------------------------------------------------

  /**
   * A plan can carry a fixed-amount adjustment, or none at all, in which case
   * readDiscountPercent returns 0 and promising "save 0%" would read as a bug.
   */
  function formatOptionLabel(plan) {
    if (!plan.discountPercent) {
      return COPY.frequencyOptionNoDiscount.replace("{cadence}", plan.label);
    }

    return COPY.frequencyOption
      .replace("{cadence}", plan.label)
      .replace("{discount}", plan.discountPercent);
  }

  /**
   * Disables the control and shows it is working. On success nothing clears this: AMP
   * repaints, the line key changes, and renderLine builds a fresh row. It is only
   * reversed when the request fails or never lands.
   */
  function setRowPending(row, isPending) {
    row.classList.toggle(INJECTED_ROW_CLASS + "--pending", isPending);

    const control = row.querySelector("button, select");

    if (control) {
      control.disabled = isPending;
    }

    const button = row.querySelector("." + INJECTED_ROW_CLASS + "__cta");

    if (button) {
      button.textContent = isPending ? COPY.pending : button.dataset.idleLabel;
    }

    const label = row.querySelector("." + INJECTED_ROW_CLASS + "__label");

    if (!label) {
      return;
    }

    const existingSpinner = label.querySelector("." + INJECTED_ROW_CLASS + "__spinner");

    if (isPending && !existingSpinner) {
      const spinner = document.createElement("span");

      spinner.className = INJECTED_ROW_CLASS + "__spinner";
      spinner.setAttribute("aria-hidden", "true");
      label.appendChild(spinner);
    } else if (!isPending && existingSpinner) {
      existingSpinner.remove();
    }
  }

  /**
   * A failed cart call that silently puts the control back looks to the customer like the
   * click did nothing, so say what happened. Same lesson as the BMSM V2.2 silent add.
   */
  function showRowError(row) {
    const existing = row.querySelector("." + INJECTED_ROW_CLASS + "__error");

    if (existing) {
      existing.remove();
    }

    const message = document.createElement("p");

    message.className = INJECTED_ROW_CLASS + "__error";
    message.setAttribute("role", "alert");
    message.textContent = COPY.error;

    row.appendChild(message);

    setTimeout(function () {
      message.remove();
    }, ERROR_VISIBLE_MS);
  }

  /**
   * One entry point for both controls so the pending state, the failure recovery and the
   * timeout are identical whether the customer upgrades or switches frequency.
   */
  function changePlan(row, lineNumber, quantity, sellingPlanId) {
    // Another line is mid-update. applySellingPlan would resolve null and this row would
    // flash an error for something that never failed, so just ignore the click.
    if (isApplyingPlan) {
      return;
    }

    setRowPending(row, true);

    const timeoutId = setTimeout(function () {
      log("no repaint within", PENDING_TIMEOUT_MS, "ms - releasing the control");
      setRowPending(row, false);
      showRowError(row);
    }, PENDING_TIMEOUT_MS);

    applySellingPlan(lineNumber, quantity, sellingPlanId).then(function (result) {
      if (result) {
        return;
      }

      clearTimeout(timeoutId);
      setRowPending(row, false);
      showRowError(row);
    });
  }

  function buildUpgradeButton(plans, onUpgrade) {
    const bestDiscount = plans.reduce(function (highest, plan) {
      return Math.max(highest, plan.discountPercent);
    }, 0);

    const button = document.createElement("button");

    button.type = "button";
    button.className = INJECTED_ROW_CLASS + "__cta";
    button.textContent = bestDiscount
      ? COPY.upgradePrompt.replace("{discount}", bestDiscount)
      : COPY.upgradePromptNoDiscount;

    // Kept so the pending state can put the original wording back on failure.
    button.dataset.idleLabel = button.textContent;
    button.addEventListener("click", onUpgrade);

    return button;
  }

  function buildFrequencySelect(plans, currentPlanId, onChange) {
    const select = document.createElement("select");

    select.className = INJECTED_ROW_CLASS + "__select";

    plans.forEach(function (plan) {
      const option = document.createElement("option");

      option.value = String(plan.id);
      option.textContent = formatOptionLabel(plan);
      option.selected = plan.id === currentPlanId;

      select.appendChild(option);
    });

    const oneTimeOption = document.createElement("option");

    oneTimeOption.value = "";
    oneTimeOption.textContent = COPY.oneTimeOption;

    select.appendChild(oneTimeOption);
    select.addEventListener("change", onChange);

    return select;
  }

  /**
   * Everything about the line that our control depends on. Quantity is in here because
   * the change.js call replays it, and the line number because a reorder must rebuild.
   */
  function buildLineKey(item, lineNumber) {
    const planId = item.selling_plan_allocation
      ? item.selling_plan_allocation.selling_plan.id
      : "";

    return [lineNumber, item.variant_id, item.quantity, planId].join("|");
  }

  /**
   * AMP renders no data attributes on a cart line, but DOM order matches cart.items
   * order (verified on AU). So the Nth line node maps to change.js line N + 1.
   *
   * AMP also RECYCLES line elements: a node showing product A is repainted to show
   * product B when the cart reorders, keeping its children. So the stamp lives on the
   * line element and covers the whole item identity. Keying on our row alone let a
   * subscribable product's control survive onto a product with no selling plans.
   */
  function renderLine(lineElement, item, lineNumber) {
    const lineKey = buildLineKey(item, lineNumber);

    // Nothing this control depends on changed, so leave the DOM alone.
    if (lineElement.dataset[LINE_KEY_FLAG] === lineKey) {
      return;
    }

    // Claim the line before awaiting, so a concurrent render does not duplicate the row.
    lineElement.dataset[LINE_KEY_FLAG] = lineKey;

    const existingRow = lineElement.querySelector("." + INJECTED_ROW_CLASS);

    if (existingRow) {
      existingRow.remove();
    }

    const currentPlanId = item.selling_plan_allocation
      ? item.selling_plan_allocation.selling_plan.id
      : null;

    fetchProduct(item.handle).then(function (product) {
      // The line may have been removed or repainted while the fetch was in flight.
      if (!lineElement.isConnected || lineElement.dataset[LINE_KEY_FLAG] !== lineKey) {
        return;
      }

      const plans = resolvePlansForVariant(product, item.variant_id);

      if (!plans.length) {
        return;
      }

      const row = document.createElement("div");

      row.className = INJECTED_ROW_CLASS;
      row.dataset[INJECTED_ROW_FLAG] = "true";

      if (currentPlanId) {
        const label = document.createElement("span");

        label.className = INJECTED_ROW_CLASS + "__label";
        label.textContent = COPY.frequencyLabel;

        row.classList.add(INJECTED_ROW_CLASS + "--subscribed");
        row.appendChild(label);
        row.appendChild(
          buildFrequencySelect(plans, currentPlanId, function (event) {
            const value = event.target.value;

            changePlan(row, lineNumber, item.quantity, value ? Number(value) : null);
          })
        );
      } else {
        row.appendChild(
          buildUpgradeButton(plans, function () {
            changePlan(row, lineNumber, item.quantity, plans[0].id);
          })
        );
      }

      lineElement.appendChild(row);
      log("rendered line", lineNumber, item.handle, plans.length + " plans");
    });
  }

  function renderAllLines(cart) {
    if (!cart || !cart.items) {
      return;
    }

    const lineElements = document.querySelectorAll(SELECTORS.cartLine);

    if (lineElements.length !== cart.items.length) {
      log("line count mismatch, skipping", lineElements.length, cart.items.length);
      return;
    }

    cart.items.forEach(function (item, index) {
      renderLine(lineElements[index], item, index + 1);
    });
  }

  // ---------------------------------------------------------------------------
  // Free gift guard
  // ---------------------------------------------------------------------------

  /**
   * Replaces ampCheckFreeGift from amp-upsell.js, which removes a free gift whenever the
   * cart total drops below the reward threshold. A subscription upgrade lowers the total
   * by the plan discount, so without this the customer loses the gift the moment they
   * subscribe. Compare against the total BEFORE subscription savings instead.
   */
  function checkFreeGiftIgnoringSubscriptionSavings(cart, cartTarget) {
    // A selling plan does not discount the line, it REPLACES the price: on a subscription
    // line original_line_price already equals final_line_price. The undiscounted figure
    // only survives in selling_plan_allocation.compare_at_price, per unit.
    const subscriptionSavings = cart.items.reduce(function (total, item) {
      const allocation = item.selling_plan_allocation;

      if (!allocation || !allocation.compare_at_price) {
        return total;
      }

      const fullLinePrice = allocation.compare_at_price * item.quantity;

      return total + Math.max(0, fullLinePrice - item.final_line_price);
    }, 0);

    const qualifyingTotal = cart.total_price + subscriptionSavings;

    cartTarget.blocks.forEach(function (block) {
      if (block.type !== "CartRewardsBlock" || !block.enabled) {
        return;
      }

      block.content.tiers.forEach(function (tier) {
        if (tier.type !== "FreeGift") {
          return;
        }

        const threshold = tier.trigger * window.Shopify.currency.rate;

        if (qualifyingTotal / 100 >= threshold) {
          return;
        }

        cart.items.forEach(function (item) {
          if (item.handle !== tier.freeGift.handle) {
            return;
          }

          const updates = {};

          updates[item.variant_id] = 0;

          fetch(window.Shopify.routes.root + "cart/update.js", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ updates: updates }),
          }).catch(function (error) {
            console.error("[boomba-sub-upsell] free gift cleanup failed", error);
          });
        });
      });
    });
  }

  // ---------------------------------------------------------------------------
  // Wiring
  // ---------------------------------------------------------------------------

  let latestCart = null;

  // Call through to the helpers declared in amp-upsell.js so nothing there is lost.
  window.AMP_CART_UPDATED = function (cart, cartTarget) {
    latestCart = cart;

    if (typeof window.ampSyncCartCounts === "function") {
      window.ampSyncCartCounts(cart.item_count);
    }

    if (typeof window.ampHideGiftQuantityButtons === "function") {
      window.ampHideGiftQuantityButtons();
    }

    if (KEEP_FREE_GIFT_FOR_SUBSCRIBERS) {
      checkFreeGiftIgnoringSubscriptionSavings(cart, cartTarget);
    } else if (typeof window.ampCheckFreeGift === "function") {
      window.ampCheckFreeGift(cart, cartTarget);
    }

    renderAllLines(cart);
  };

  window.AMP_CART_LOADED = function (cart) {
    latestCart = cart;
    renderAllLines(cart);
  };

  /**
   * AMP patches the drawer in place rather than rebuilding it, so injected rows usually
   * survive a cart update. The observer covers the cases where it does rebuild, such as
   * the drawer being closed and reopened.
   */
  function startObserver() {
    const root = document.querySelector(SELECTORS.ampRoot);

    if (!root) {
      return;
    }

    let pendingRender = null;

    // A line with no selling plans correctly has no row, so "is a row missing" is not a
    // usable trigger. Re-run instead and let the per-line key make repeats a no-op.
    // Debounced because the announcement carousel also mutates this subtree.
    const observer = new MutationObserver(function () {
      if (!latestCart || pendingRender) {
        return;
      }

      pendingRender = setTimeout(function () {
        pendingRender = null;
        renderAllLines(latestCart);
      }, 150);
    });

    observer.observe(root, { childList: true, subtree: true });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", startObserver);
  } else {
    startObserver();
  }

  // Debug surface: window.BOOMBA_SUB_UPSELL.render() re-renders against the last cart.
  window.BOOMBA_SUB_UPSELL = {
    version: "1.2.3",
    config: { SELECTORS: SELECTORS, COPY: COPY, KEEP_FREE_GIFT_FOR_SUBSCRIBERS: KEEP_FREE_GIFT_FOR_SUBSCRIBERS },
    getCart: function () {
      return latestCart;
    },
    render: function () {
      renderAllLines(latestCart);
    },
    resolvePlansForVariant: resolvePlansForVariant,
    applySellingPlan: applySellingPlan,
  };
})();
